import L from 'leaflet';

/**
 * Map Layer Configuration
 *
 * Provides tile layer configurations for different map visualizations:
 * - Esri World Imagery (satellite)
 * - OpenStreetMap (streets)
 * - Hybrid (satellite + street labels)
 *
 * @module config/mapLayers
 */

// =============================================================================
// URL CONSTANTS
// =============================================================================

/**
 * Esri World Imagery Tile Layer URL
 * Provides high-quality satellite imagery for global coverage
 * @constant {string}
 */
export const ESRI_SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

/**
 * Esri World Imagery Attribution (MANDATORY)
 * Must be displayed when using Esri satellite imagery
 * @constant {string}
 */
export const ESRI_ATTRIBUTION = '&copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community';

/**
 * OpenStreetMap Tile Layer URL
 * Provides standard street map visualization
 * @constant {string}
 * @deprecated Use CARTO_VOYAGER_URL instead to avoid referer blocking issues
 */
export const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

/**
 * OpenStreetMap Attribution
 * Must be displayed when using OSM tiles
 * @constant {string}
 * @deprecated Use CARTO_ATTRIBUTION instead
 */
export const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';

/**
 * CartoDB Voyager Tile Layer URL
 * Provides modern street map visualization without referer restrictions
 * This is the recommended replacement for OSM to avoid "Referer is required" errors
 * @constant {string}
 */
export const CARTO_VOYAGER_URL = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';

/**
 * CartoDB Attribution
 * Must be displayed when using CartoDB tiles
 * Includes both OSM and CARTO attributions as required
 * @constant {string}
 */
export const CARTO_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OSM</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener noreferrer">CARTO</a>';

/**
 * Fallback URL for Esri service (alternative satellite imagery source)
 * Uses a different Esri server in case primary is unavailable
 * @constant {string}
 */
export const ESRI_FALLBACK_URL = 'https://tiles.arcgis.com/tiles/P3ePLMYs2RVChkJx/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

// =============================================================================
// LAYER CREATION FUNCTIONS
// =============================================================================

/**
 * Create Esri World Imagery satellite tile layer
 *
 * @param {Object} [options={}] - Layer configuration options
 * @param {number} [options.opacity=1.0] - Layer opacity (0-1)
 * @param {boolean} [options.useFallback=false] - Use fallback URL if primary fails
 * @returns {L.TileLayer} Configured Esri satellite layer
 *
 * @example
 * const satelliteLayer = createEsriLayer({ opacity: 0.8 });
 * satelliteLayer.addTo(map);
 */
export function createEsriLayer(options = {}) {
  const {
    opacity = 1.0,
    useFallback = false
  } = options;

  const url = useFallback ? ESRI_FALLBACK_URL : ESRI_SATELLITE_URL;

  return L.tileLayer(url, {
    attribution: ESRI_ATTRIBUTION,
    opacity: opacity,
    maxZoom: 19,
    minZoom: 0,
    // Performance optimizations
    updateWhenIdle: true,        // Load tiles when not interacting
    updateWhenZooming: false,     // Don't load during zoom animation
    keepBuffer: 2,                // Pre-load 2 zoom levels
    // Enable browser caching
    crossOrigin: true,
    detectRetina: true,
    // Error handling with retry support
    errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    className: 'map-tile-satellite',
    // Network optimization
    subdomains: 'abc',            // Use subdomains for parallel loading
    // Tile loading optimization
    bounds: undefined,            // No bounds restriction
    noWrap: false                 // Allow horizontal wrapping
  });
}

/**
 * Create OpenStreetMap street tile layer
 *
 * @param {Object} [options={}] - Layer configuration options
 * @param {number} [options.opacity=1.0] - Layer opacity (0-1)
 * @returns {L.TileLayer} Configured OSM street layer
 * @deprecated Use createCartoLayer instead to avoid referer blocking issues
 *
 * @example
 * const streetLayer = createOSMLayer({ opacity: 1.0 });
 * streetLayer.addTo(map);
 */
export function createOSMLayer(options = {}) {
  const {
    opacity = 1.0
  } = options;

  return L.tileLayer(OSM_URL, {
    attribution: OSM_ATTRIBUTION,
    opacity: opacity,
    maxZoom: 19,
    minZoom: 0,
    subdomains: 'abc',
    // Performance optimizations
    updateWhenIdle: true,
    updateWhenZooming: false,
    keepBuffer: 2,
    // Enable browser caching
    crossOrigin: true,
    detectRetina: true,
    className: 'map-tile-streets'
  });
}

/**
 * Create CartoDB Voyager street tile layer
 *
 * This is the recommended function for street map visualization.
 * CartoDB does not block requests based on referer, unlike OpenStreetMap.
 *
 * @param {Object} [options={}] - Layer configuration options
 * @param {number} [options.opacity=1.0] - Layer opacity (0-1)
 * @returns {L.TileLayer} Configured CartoDB street layer
 *
 * @example
 * const streetLayer = createCartoLayer({ opacity: 1.0 });
 * streetLayer.addTo(map);
 */
export function createCartoLayer(options = {}) {
  const {
    opacity = 1.0
  } = options;

  return L.tileLayer(CARTO_VOYAGER_URL, {
    attribution: CARTO_ATTRIBUTION,
    opacity: opacity,
    maxZoom: 19,
    minZoom: 0,
    subdomains: 'abcd', // CartoDB uses a, b, c, d subdomains
    // Performance optimizations
    updateWhenIdle: true,
    updateWhenZooming: false,
    keepBuffer: 2,
    // Enable browser caching
    crossOrigin: true,
    detectRetina: true,
    className: 'map-tile-streets'
  });
}

/**
 * Create hybrid layer (satellite + street labels)
 *
 * Combines Esri World Imagery with semi-transparent CartoDB overlay
 * to provide satellite imagery with street and location labels
 *
 * @param {Object} [options={}] - Layer configuration options
 * @param {number} [options.satelliteOpacity=1.0] - Satellite layer opacity (0-1)
 * @param {number} [options.labelOpacity=0.6] - Label overlay opacity (0-1)
 * @param {boolean} [options.useFallback=false] - Use fallback URL for satellite
 * @returns {L.LayerGroup} Combined hybrid layer with both satellite and labels
 *
 * @example
 * const hybridLayer = createHybridLayer({ satelliteOpacity: 1.0, labelOpacity: 0.6 });
 * hybridLayer.addTo(map);
 */
export function createHybridLayer(options = {}) {
  const {
    satelliteOpacity = 1.0,
    labelOpacity = 0.6,
    useFallback = false
  } = options;

  const satelliteLayer = createEsriLayer({
    opacity: satelliteOpacity,
    useFallback
  });

  const labelLayer = L.tileLayer(CARTO_VOYAGER_URL, {
    attribution: '', // Attribution handled by satellite layer
    opacity: labelOpacity,
    maxZoom: 19,
    minZoom: 0,
    subdomains: 'abcd', // CartoDB uses a, b, c, d subdomains
    updateWhenIdle: true,
    updateWhenZooming: false,
    keepBuffer: 2,
    crossOrigin: true,
    detectRetina: true,
    className: 'map-tile-labels'
  });

  // Combine both layers into a layer group
  return L.layerGroup([satelliteLayer, labelLayer]);
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get attribution text for a given layer type
 *
 * @param {string} layerType - Type of layer ('satellite' | 'hybrid' | 'streets')
 * @returns {string} Attribution HTML string
 */
export function getAttributionForLayer(layerType) {
  switch (layerType) {
    case 'satellite':
    case 'hybrid':
      return ESRI_ATTRIBUTION;
    case 'streets':
    default:
      return CARTO_ATTRIBUTION;
  }
}

/**
 * Validate layer type string
 *
 * @param {string} layerType - Layer type to validate
 * @returns {boolean} True if valid layer type
 */
export function isValidLayerType(layerType) {
  return ['satellite', 'hybrid', 'streets'].includes(layerType);
}
